import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

export const EASE = [0.22, 1, 0.36, 1];

export const btnPrimary =
  "group inline-flex items-center gap-2 rounded-full bg-electric px-6 py-3 text-sm font-semibold text-ink transition-colors duration-300 hover:bg-teal";
export const btnGhost =
  "group inline-flex items-center gap-2 rounded-full border border-line px-6 py-3 text-sm font-semibold text-mist transition-colors duration-300 hover:border-electric/60 hover:text-electric";

export function ArrowIcon() {
  return <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />;
}

export function usePageMeta(title, description) {
  useEffect(() => {
    document.title = title;
    if (description) {
      const m = document.querySelector('meta[name="description"]');
      if (m) m.setAttribute("content", description);
    }
  }, [title, description]);
}

export function Reveal({ children, delay = 0, y = 28, className = "" }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

export function MaskedLine({ children, delay = 0, className = "", mount = false }) {
  const anim = mount
    ? { animate: { y: 0 } }
    : { whileInView: { y: 0 }, viewport: { once: true, margin: "-40px" } };
  return (
    <span className="block overflow-hidden pb-[0.09em] -mb-[0.09em]">
      <motion.span
        className={`block ${className}`}
        initial={{ y: "115%" }}
        {...anim}
        transition={{ duration: 1.05, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export function Eyebrow({ children, className = "" }) {
  return (
    <p className={`flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.28em] text-electric ${className}`}>
      <span className="h-px w-8 bg-electric/60" aria-hidden="true" />
      {children}
    </p>
  );
}

export function SectionHead({ eyebrow, title, lead, center = false, className = "" }) {
  const label = eyebrow ? eyebrow.replace(/^Chapter \d+ — /, "").replace(/^\d{1,2} — /, "") : eyebrow;
  return (
    <Reveal className={`${center ? "text-center" : ""} ${className}`}>
      {label && <Eyebrow className={center ? "justify-center" : ""}>{label}</Eyebrow>}
      <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-mist">
        {title}
      </h2>
      {lead && (
        <p className={`mt-5 max-w-2xl text-base sm:text-lg leading-relaxed text-mute ${center ? "mx-auto" : ""}`}>
          {lead}
        </p>
      )}
    </Reveal>
  );
}

export function PageHero({ eyebrow, lines, lead, children }) {
  return (
    <section className="relative overflow-hidden border-b border-line bg-grid pt-36 pb-20 sm:pt-44 sm:pb-24" data-testid="page-hero">
      <div className="pointer-events-none absolute -top-40 left-1/2 h-[480px] w-[820px] -translate-x-1/2 bg-glow-blue" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-6">
        <Reveal y={16}>
          <Eyebrow>{eyebrow}</Eyebrow>
        </Reveal>
        <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-mist">
          {lines.map((line, i) => (
            <MaskedLine key={i} delay={0.12 + i * 0.12} mount>
              {line}
            </MaskedLine>
          ))}
        </h1>
        {lead && (
          <motion.p
            className="mt-7 max-w-2xl text-base sm:text-lg leading-relaxed text-mute"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5, ease: EASE }}
          >
            {lead}
          </motion.p>
        )}
        {children && (
          <motion.div
            className="mt-9 flex flex-wrap gap-4"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
          >
            {children}
          </motion.div>
        )}
      </div>
    </section>
  );
}

export function Marquee({ items }) {
  const row = (key) => (
    <div key={key} className="flex shrink-0 items-center">
      {items.map((item, i) => (
        <span key={`${key}-${i}`} className="flex items-center font-display text-lg sm:text-xl font-medium tracking-tight text-mute">
          <span className="px-6 sm:px-8">{item}</span>
          <span className="h-1.5 w-1.5 rounded-full bg-electric/50" aria-hidden="true" />
        </span>
      ))}
    </div>
  );
  return (
    <div className="relative overflow-hidden border-y border-line bg-abyss/60 py-5" data-testid="editorial-marquee" aria-hidden="true">
      <div className="marquee-track">
        {row("a")}
        {row("b")}
      </div>
      <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-ink to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-ink to-transparent" />
    </div>
  );
}

export function CountUp({ to, suffix = "", className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 1.8,
      ease: EASE,
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to]);
  return (
    <span ref={ref} className={className}>
      {val.toLocaleString("en-IN")}
      {suffix}
    </span>
  );
}

const TONES = {
  dev: "border-teal/30 bg-teal/10 text-teal",
  planned: "border-electric/30 bg-electric/10 text-electric",
  future: "border-line bg-white/5 text-mute",
  target: "border-amber/30 bg-amber/10 text-amber",
};

export function Tag({ children, tone = "planned", className = "" }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] ${TONES[tone]} ${className}`}>
      {children}
    </span>
  );
}

export function Card({ children, className = "", testid }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl border border-line bg-panel/70 shadow-[0_1px_3px_rgba(11,21,38,0.05)] backdrop-blur-sm transition-[border-color,box-shadow] duration-300 hover:border-electric/40 hover:shadow-[0_12px_32px_rgba(11,21,38,0.09)] ${className}`}
    >
      {children}
    </div>
  );
}

export function TextLink({ to, children, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-electric transition-colors duration-300 hover:text-teal"
    >
      {children}
      <ArrowIcon />
    </Link>
  );
}
